import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import {WaterTypeService} from '../../../../../services/waterType.service';
import WaterTypeModel from '../../../../../services/waterType.model';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * WaterTypeComponent
 *
 * This component is responsible for managing Water Types within the Pond Management module.
 * It provides a user interface for administrators to view, create, update, and search for water types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all water types.
 * - Provide search functionality to filter water types by name or abbreviation.
 * - Manage the creation of new water types with validation.
 * - Handle updates to existing water types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update water types.
 * - **Real-time Search**: Filter list by water type name or abbreviation.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures water type names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all water types from `WaterTypeService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateWaterType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-water-type
 * @example
 * <app-water-type></app-water-type>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-water-type',
  templateUrl: './water-type.component.html',
  styleUrls: ['./water-type.component.scss']
})
export class WaterTypeComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched water types from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {WaterTypeModel[]}
   * @default []
   */
  public waterTypes: WaterTypeModel[] = [];

  /**
   * Array holding the currently displayed water types.
   * This list is populated by the BatchLoader (subset of waterTypes or filteredWaterTypes).
   * @type {WaterTypeModel[]}
   * @default []
   */
  public waterTypesList: WaterTypeModel[] = [];

  /**
   * Array holding water types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {WaterTypeModel[]}
   * @default []
   */
  public filteredWaterTypes: WaterTypeModel[] = [];

  /**
   * Model for the current water type being added or edited.
   * Bound to the form for data manipulation.
   * @type {WaterTypeModel}
   */
  public waterType: WaterTypeModel = new WaterTypeModel();

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Reactive form group for water type details (name, abbreviation, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public waterTypeDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for WaterTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {WaterTypeService} waterTypeService - Service for Water Type API operations.
   * @service WaterTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public waterTypeService: WaterTypeService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.waterType = new WaterTypeModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all water types from the backend.
   * 5. Formats the 'updatedAt' date for each water type.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.waterTypes = [];
    this.waterTypesList = [];
    this.filteredWaterTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.waterTypeDoubleClick = false;
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      if (this.waterTypes.length > 0) {
        this.waterTypes.forEach(eachWaterType => {
          eachWaterType.updatedAt = JSON.parse(JSON.stringify(moment(eachWaterType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredWaterTypes : this.waterTypes,
      (updatedList) => {
        this.waterTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method waterTypeFormValidation
   * @returns {AbstractControl} Form controls
   */
  public get waterTypeFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the water type list based on the search query.
   *
   * - Filters by name or abbreviation (case-insensitive).
   * - Updates `filteredWaterTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredWaterTypes`, `waterTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.waterTypes.filter(water =>
        water.name?.toLowerCase().includes(query) ||
        water.abbreviation?.toLowerCase().includes(query)
      )
      : this.waterTypes;
    this.filteredWaterTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.waterTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.waterType = new WaterTypeModel();
    this.waterTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Water Type form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateWaterType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateWaterType() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.waterTypeDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      if (this.submitButton) {
        await this.waterTypeService.create(formData);
        this.toasterService.success('Water type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateWaterType = {...formData}
        delete updateWaterType.id;
        await this.waterTypeService.updateOneByID(this.waterType.id, updateWaterType);
        this.toasterService.success('Water type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.waterTypeDoubleClick = false;
    }
  }

  /**
   * Retrieves a Water Type by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getWaterTypeById
   * @param {string} id - The ID of the water type to edit.
   * @returns {void}
   */
  public getWaterTypeById(id: string) {
    this.showForm = true;
    this.waterTypeDoubleClick = false;
    this.updateButton = true;
    const foundWaterType = this.waterTypesList.find(uom => uom?.id === id);
    this.waterType = foundWaterType ? {...foundWaterType} : new WaterTypeModel(); // clone object
    if (this.waterType) {
      this.registerForm.patchValue({
        name: this.waterType.name,
        abbreviation: this.waterType.abbreviation,
        description: this.waterType.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
